import { useEffect, useState } from 'react';
import { ToggleSwitch } from "./ToggleSwitch";
import { createForm, getFormById, patchForm } from "../services/apiService";
import { FormFA } from "../interfaces/global-interfaces";
import {
    Check,
    Code2,
    Copy,
    Eye,
    EyeOff,
    Globe,
    Link2,
    Mail,
    Plus,
    Power,
    ShieldCheck,
    Sparkles,
    X,
} from "lucide-react";
import { Card } from "./ui/Card";
import { Button } from "./ui/Button";
import { Input, Label } from "./ui/Input";
import { Section } from "./ui/Section";
import { Tabs } from "./ui/Tabs";
import { ConfirmModal } from "./ConfirmModal";
import { useToast } from "./ui/Toast";

export const UpsertProjectForm = ({
                                      onSubmit,
                                      initialData,
                                  }: {
    onSubmit: (formData: FormFA) => void;
    initialData?: { project_id: string; form_id?: string };
}) => {
    const [projectName, setProjectName] = useState('');
    const [copied, setCopied] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);
    const [activeTab, setActiveTab] = useState<'integration' | 'settings'>('integration');
    const [formEnabled, setFormEnabled] = useState(true);
    const [captchaSecret, setCaptchaSecret] = useState('');
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [acceptedDomains, setAcceptedDomains] = useState<string[]>([]);
    const [newDomain, setNewDomain] = useState('');
    const [webhooks, setWebhooks] = useState<string[]>([]);
    const [newWebhook, setNewWebhook] = useState('');
    const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
    const [newRecipient, setNewRecipient] = useState('');
    const [recipientPendingRemoval, setRecipientPendingRemoval] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const {showToast} = useToast();

    const generatedUrl = initialData
        ? `https://api.formsaware.com/projects/${initialData.project_id || ""}/forms/${initialData.form_id || ""}/submissions`
        : "";

    const codeSnippet = `<form action="${generatedUrl}" method="POST">
  <input type="email" name="email" placeholder="Your email" required />
  <textarea name="message" placeholder="Your message" required></textarea>
  <button type="submit">Send</button>
</form>`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(generatedUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleCreate = async () => {
        const formData: FormFA = {
            name: projectName.trim(),
            accepted_domains: acceptedDomains,
            webhooks,
            captcha_secret: captchaSecret || "",
            recipient_emails: recipientEmails,
            enabled: formEnabled,
            captcha_enabled: !!captchaSecret,
        };

        setSaving(true);
        try {
            if (initialData?.form_id) {
                const updatedForm = await patchForm(initialData.project_id, initialData.form_id, formData);
                onSubmit(updatedForm);
            } else {
                const newForm = await createForm(initialData!.project_id, formData);
                onSubmit(newForm);
            }
        } catch (err) {
            console.error("Error saving form:", err);
            showToast("Couldn't save this form. Please try again.", "error");
        } finally {
            setSaving(false);
        }
    };

    const updateSettings = async (updates: Partial<FormFA>) => {
        if (!initialData?.project_id || !initialData?.form_id) return;
        try {
            setSaving(true);
            await patchForm(initialData.project_id, initialData.form_id, updates);
        } catch (err) {
            console.error("Error updating settings:", err);
            showToast("Couldn't save this change. Please try again.", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleToggleFormEnabled = async (value: boolean) => {
        setFormEnabled(value);
        await updateSettings({ enabled: value });
    };
    const handleAddRecipient = () => {
        if (newRecipient.trim() && !recipientEmails.includes(newRecipient.trim())) {
            const updated = [...recipientEmails, newRecipient.trim()];
            setRecipientEmails(updated);
            setNewRecipient('');
            updateSettings({ recipient_emails: updated });
        }
    };

    const handleRemoveRecipient = (index: number) => {
        const updated = recipientEmails.filter((_, i) => i !== index);
        setRecipientEmails(updated);
        updateSettings({ recipient_emails: updated });
    };

    useEffect(() => {
        const fetchFormData = async () => {
            if (initialData?.project_id && initialData?.form_id) {
                try {
                    const form = await getFormById(initialData.project_id, initialData.form_id);
                    setProjectName(form.name || "");
                    setAcceptedDomains(form.accepted_domains || []);
                    setWebhooks(form.webhooks || []);
                    setFormEnabled(form.enabled ?? true);
                    setCaptchaSecret(form.captcha_secret || "");
                    setRecipientEmails(form.recipient_emails || []);
                } catch (err) {
                    console.error("Error fetching form data:", err);
                    showToast("Couldn't load this form's data.", "error");
                }
            }
        };
        fetchFormData();
    }, [initialData, showToast]);

    const handleAddDomain = () => {
        if (newDomain.trim() && !acceptedDomains.includes(newDomain.trim())) {
            setAcceptedDomains([...acceptedDomains, newDomain.trim()]);
            setNewDomain('');
        }
    };

    const handleRemoveDomain = (index: number) => {
        setAcceptedDomains(acceptedDomains.filter((_, i) => i !== index));
    };

    const handleAddWebhook = () => {
        if (newWebhook.trim() && !webhooks.includes(newWebhook.trim())) {
            setWebhooks([...webhooks, newWebhook.trim()]);
            setNewWebhook('');
        }
    };

    const handleRemoveWebhook = (index: number) => {
        setWebhooks(webhooks.filter((_, i) => i !== index));
    };

    const Chip = ({value, onRemove, mono, icon: Icon}: {
        value: string; onRemove: () => void; mono?: boolean; icon: typeof Globe;
    }) => (
        <div className="flex items-center gap-2 bg-surface-sunken rounded-lg px-3 py-2">
            <Icon size={13} className="text-ink-faint shrink-0"/>
            <span className={`flex-1 text-sm text-ink break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
            <button
                onClick={onRemove}
                className="size-6 shrink-0 flex items-center justify-center rounded-md text-ink-faint hover:text-error hover:bg-error-tint transition-colors"
                aria-label="Remove"
            >
                <X size={13}/>
            </button>
        </div>
    );

    const AddRow = ({value, onChange, onAdd, placeholder, type = "text"}: {
        value: string; onChange: (v: string) => void; onAdd: () => void; placeholder: string; type?: string;
    }) => (
        <div className="flex items-center gap-2">
            <Input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onAdd();
                    }
                }}
                placeholder={placeholder}
                className="flex-1"
            />
            <Button size="sm" icon={Plus} onClick={onAdd} type="button">
                Add
            </Button>
        </div>
    );

    return (
        <div className="flex flex-col gap-6 w-full mx-auto">
            <ConfirmModal
                isOpen={recipientPendingRemoval !== null}
                title="Remove recipient"
                message={`Stop sending notifications to ${recipientPendingRemoval !== null ? recipientEmails[recipientPendingRemoval] : ''}?`}
                showDeleteButton
                onConfirm={() => {
                    if (recipientPendingRemoval !== null) handleRemoveRecipient(recipientPendingRemoval);
                    setRecipientPendingRemoval(null);
                }}
                onCancel={() => setRecipientPendingRemoval(null)}
            />

            <Tabs
                tabs={[
                    {key: 'integration', label: 'Integration', icon: Link2},
                    {key: 'settings', label: 'Settings', icon: ShieldCheck},
                ]}
                active={activeTab}
                onChange={(key) => setActiveTab(key as 'integration' | 'settings')}
            />

            {/* --- Integration Tab --- */}
            {activeTab === 'integration' && (
                <Card className="divide-y divide-border [&>*]:py-6 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0 animate-fade-in animate-duration-200">
                    <Section icon={Sparkles} title="Basics">
                        <div>
                            <Label>Project Name</Label>
                            <Input
                                type="text"
                                placeholder="My awesome project"
                                value={projectName}
                                onChange={(e) => setProjectName(e.target.value)}
                            />
                        </div>
                    </Section>

                    {projectName.trim() && (
                        <>
                            <Section icon={Link2} title="Form Endpoint" description="Send POST requests here to create submissions.">
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-surface-sunken px-4 py-2.5 rounded-xl font-mono text-xs break-all text-ink">
                                        {generatedUrl}
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        icon={copied ? Check : Copy}
                                        onClick={handleCopy}
                                        type="button"
                                    >
                                        {copied ? 'Copied' : 'Copy'}
                                    </Button>
                                </div>
                            </Section>

                            <Section icon={Code2} title="Code Example" description="Drop this into any static page to start collecting submissions.">
                                <div className="rounded-xl border border-border overflow-hidden">
                                    <div className="flex items-center justify-between gap-3 px-4 py-2 bg-surface-sunken border-b border-border">
                                        <div className="flex items-center gap-1.5">
                                            <span className="size-2.5 rounded-full bg-error/50"/>
                                            <span className="size-2.5 rounded-full bg-warning/50"/>
                                            <span className="size-2.5 rounded-full bg-success/50"/>
                                            <span className="text-xs font-mono text-ink-faint ml-2">contact-form.html</span>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            icon={codeCopied ? Check : Copy}
                                            type="button"
                                            onClick={async () => {
                                                await navigator.clipboard.writeText(codeSnippet);
                                                setCodeCopied(true);
                                                setTimeout(() => setCodeCopied(false), 2000);
                                            }}
                                        >
                                            {codeCopied ? "Copied" : "Copy"}
                                        </Button>
                                    </div>
                                    <pre className="p-4 text-xs overflow-x-auto whitespace-pre font-mono text-ink bg-surface">{codeSnippet}</pre>
                                </div>
                            </Section>

                            <Section icon={Globe} title="Accepted Domains" description="Requests will only be accepted from these origins.">
                                <div className="space-y-2">
                                    <AddRow value={newDomain} onChange={setNewDomain} onAdd={handleAddDomain} placeholder="example.com"/>
                                    {acceptedDomains.length > 0 && (
                                        <div className="space-y-1.5 pt-1">
                                            {acceptedDomains.map((domain, index) => (
                                                <Chip key={index} value={domain} icon={Globe} onRemove={() => handleRemoveDomain(index)}/>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Section>

                            <Section icon={Link2} title="Webhooks" description="We'll POST every new submission to these URLs.">
                                <div className="space-y-2">
                                    <AddRow value={newWebhook} onChange={setNewWebhook} onAdd={handleAddWebhook} placeholder="https://your-webhook-url" type="url"/>
                                    {webhooks.length > 0 && (
                                        <div className="space-y-1.5 pt-1">
                                            {webhooks.map((url, index) => (
                                                <Chip key={index} value={url} icon={Link2} mono onRemove={() => handleRemoveWebhook(index)}/>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Section>
                        </>
                    )}
                </Card>
            )}

            {/* --- Settings Tab --- */}
            {activeTab === 'settings' && (
                <Card padding="none" className="divide-y divide-border animate-fade-in animate-duration-200">
                    <div className="flex items-start justify-between gap-4 p-5">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className="size-9 shrink-0 rounded-xl bg-primary-tint text-primary flex items-center justify-center">
                                <Power size={16}/>
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-semibold text-sm text-ink">Form Enabled</h3>
                                <p className="text-sm text-ink-muted mt-0.5">Allow form submissions for this endpoint.</p>
                            </div>
                        </div>
                        <ToggleSwitch
                            checked={formEnabled}
                            onChange={handleToggleFormEnabled}
                        />
                    </div>

                    <div className="p-5">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="size-9 shrink-0 rounded-xl bg-info-tint text-info flex items-center justify-center">
                                <ShieldCheck size={16}/>
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-semibold text-sm text-ink">Captcha Secret</h3>
                                <p className="text-sm text-ink-muted mt-0.5">Used to verify submissions came from a real user.</p>
                            </div>
                        </div>
                        <div className="relative w-full pl-12">
                            <Input
                                type={showCaptcha ? "text" : "password"}
                                value={captchaSecret}
                                onChange={(e) => setCaptchaSecret(e.target.value)}
                                placeholder="no secret"
                                className="pr-11"
                            />
                            <button
                                type="button"
                                onClick={() => setShowCaptcha(!showCaptcha)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors"
                                aria-label={showCaptcha ? "Hide secret" : "Show secret"}
                            >
                                {showCaptcha ? <EyeOff size={16}/> : <Eye size={16}/>}
                            </button>
                        </div>
                    </div>

                    <div className="p-5">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="size-9 shrink-0 rounded-xl bg-success-tint text-success flex items-center justify-center">
                                <Mail size={16}/>
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-semibold text-sm text-ink">Notification Recipients</h3>
                                <p className="text-sm text-ink-muted mt-0.5">Get an email every time this form receives a submission.</p>
                            </div>
                        </div>
                        <div className="pl-12 space-y-2">
                            <AddRow value={newRecipient} onChange={setNewRecipient} onAdd={handleAddRecipient} placeholder="email@example.com" type="email"/>
                            {recipientEmails.length > 0 && (
                                <div className="space-y-1.5 pt-1">
                                    {recipientEmails.map((email, index) => (
                                        <Chip key={index} value={email} icon={Mail} onRemove={() => setRecipientPendingRemoval(index)}/>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* Save */}
            <div className="flex justify-end">
                <Button icon={Check} onClick={handleCreate} loading={saving}>
                    {initialData?.form_id ? 'Save changes' : 'Create Form'}
                </Button>
            </div>
        </div>
    );
};
